import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../auth/AuthProvider'
import MashfaLogo from '../components/MashfaLogo'
import { OtpInput } from '../components/OtpInput'
import { TwoFactorSetup } from '../components/TwoFactorSetup'
import './Login.css'

const APP_VERSION = __APP_VERSION__

type Stage =
  | { kind: 'password' }
  | { kind: 'otp'; session_token: string }
  | { kind: 'recovery'; session_token: string }
  | { kind: 'setup'; setup_token: string }

/** Map an error code thrown by AuthProvider into a user-facing string. */
function translateAuthError(
  code: string, t: ReturnType<typeof useTranslation>['t'],
): string {
  if (code === 'too_many_attempts') {
    return t('auth.2fa.lockedOut', 'Zu viele Versuche. Bitte einige Minuten warten.')
  }
  if (code === 'otp_invalid') {
    return t('auth.2fa.invalid', 'Falscher Code — versuche es nochmal.')
  }
  if (code === 'recovery_invalid') {
    return t('auth.recovery.invalid', 'Recovery-Code ungültig oder bereits verwendet.')
  }
  return t('auth.loginError', 'Anmeldung fehlgeschlagen.')
}

export default function Login() {
  const { t, i18n } = useTranslation()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [remember, setRemember] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [stage, setStage] = useState<Stage>({ kind: 'password' })
  const [otpCode, setOtpCode] = useState('')
  const [recoveryCode, setRecoveryCode] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const auth = useAuth()
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)
    try {
      const outcome = await auth.login(username, password)
      if (outcome.kind === 'authenticated') {
        navigate('/')
      } else if (outcome.kind === 'otp_required') {
        setStage({ kind: 'otp', session_token: outcome.session_token })
      } else if (outcome.kind === 'setup_required') {
        setStage({ kind: 'setup', setup_token: outcome.setup_token })
      }
    } catch (err) {
      const code = err instanceof Error ? err.message : 'login_failed'
      setError(translateAuthError(code, t))
    } finally {
      setIsLoading(false)
    }
  }

  const submitOtp = async (code: string) => {
    if (stage.kind !== 'otp') return
    setError(null); setIsLoading(true)
    try {
      await auth.verifyOtp(stage.session_token, code)
      navigate('/')
    } catch (err) {
      const errCode = err instanceof Error ? err.message : 'otp_invalid'
      setError(translateAuthError(errCode, t))
      setOtpCode('')
    } finally { setIsLoading(false) }
  }

  const submitRecovery = async () => {
    if (stage.kind !== 'recovery') return
    setError(null); setIsLoading(true)
    try {
      await auth.verifyRecovery(stage.session_token, recoveryCode.trim())
      navigate('/')
    } catch (err) {
      const errCode = err instanceof Error ? err.message : 'recovery_invalid'
      setError(translateAuthError(errCode, t))
    } finally { setIsLoading(false) }
  }

  // trust signals — icons use currentColor (theme gold via --login-accent)
  const trust = [
    {
      label: t('login.trustOffline'),
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><line x1="2" y1="2" x2="22" y2="22" /><path d="M8.5 16.4a5 5 0 0 1 7 0" /><path d="M5 12.9a10 10 0 0 1 3.1-1.9" /><path d="M19 12.9a10 10 0 0 0-3.6-2" /><path d="M2 8.8a16 16 0 0 1 4.6-2.6" /><path d="M22 8.8a16 16 0 0 0-7.2-3.5" /><line x1="12" y1="20" x2="12.01" y2="20" /></svg>,
    },
    {
      label: t('login.trustLocal'),
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="7" rx="2" /><rect x="3" y="13" width="18" height="7" rx="2" /><line x1="7" y1="7.5" x2="7.01" y2="7.5" /><line x1="7" y1="16.5" x2="7.01" y2="16.5" /></svg>,
    },
    {
      label: t('login.trustRoles'),
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="3.2" /><path d="M3.5 19c0-3 2.6-4.8 5.5-4.8s5.5 1.8 5.5 4.8" /><circle cx="17.5" cy="9" r="2.3" /><path d="M16.2 14.5c2.1.4 3.8 1.8 3.8 4" /></svg>,
    },
    {
      label: t('login.trustLangs'),
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><line x1="3" y1="12" x2="21" y2="12" /><path d="M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18" /></svg>,
    },
  ]

  return (
    <div className="mashfa-login" lang={i18n.language} dir={i18n.dir()}>
      <div className="login-shell">

        {/* ───── Brand side (grid auto-flips to the right in RTL) ───── */}
        <aside className="login-brand">
          <MashfaLogo className="login-logo" ariaHidden={false} />
          <div className="login-hero">
            <div className="login-eyebrow">{t('login.eyebrow')}</div>
            <h1 className="login-headline">{t('login.headline')}</h1>
            <p className="login-hero-sub">{t('login.heroSub')}</p>
            <ul className="login-trust">
              {trust.map((item, i) => (
                <li key={item.label} className="login-trust-item" style={{ '--i': i } as React.CSSProperties}>
                  {item.icon}
                  <span>{item.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* ───── Card side ───── */}
        <main className="login-card-wrap">
          <div className="login-card">

            {/* Stage: password */}
            {stage.kind === 'password' && (
              <>
                <div className="login-card-head">
                  <div className="login-card-eyebrow">{t('login.welcomeBack')}</div>
                  <h2 className="login-card-title">{t('auth.signIn')}</h2>
                  <p className="login-card-sub">{t('auth.loginSubtitle')}</p>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="login-field">
                    <label className="login-label" htmlFor="username">
                      <svg className="login-label-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-6 8-6s8 2 8 6" /></svg>
                      {t('auth.username')}
                    </label>
                    <input
                      type="text" id="username" className="login-input" dir="ltr"
                      placeholder="superadmin" value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      autoComplete="username" disabled={isLoading} autoFocus required
                    />
                  </div>
                  <div className="login-field">
                    <label className="login-label" htmlFor="password">
                      <svg className="login-label-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
                      {t('auth.password')}
                    </label>
                    <div className="login-pw-wrap">
                      <input
                        type={showPassword ? 'text' : 'password'} id="password" className="login-input" dir="ltr"
                        placeholder="••••••••" value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="current-password" disabled={isLoading} required
                      />
                      <button
                        type="button" className="login-pw-toggle"
                        onClick={() => setShowPassword((v) => !v)}
                        disabled={isLoading}
                        aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                        title={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                      >
                        {showPassword ? (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" /><path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" /><line x1="2" x2="22" y1="2" y2="22" /><path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" /></svg>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></svg>
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="login-row">
                    <label className="login-remember">
                      <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
                      <span>{t('auth.rememberMe')}</span>
                    </label>
                    <button type="button" className="login-forgot">{t('auth.forgotPassword')}</button>
                  </div>
                  <button type="submit" className="login-cta" disabled={isLoading}>
                    <span>{isLoading ? t('auth.signingIn') : t('auth.signIn')}</span>
                    <svg viewBox="0 0 24 24">
                      <line x1="3" y1="12" x2="17" y2="12" />
                      <polyline points="12 7 17 12 12 17" />
                    </svg>
                  </button>
                  {error && (
                    <div className="login-error" role="alert">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                      <span>{error}</span>
                    </div>
                  )}
                </form>
              </>
            )}

            {/* Stage: OTP */}
            {stage.kind === 'otp' && (
              <div data-testid="login-otp-stage">
                <div className="login-card-head">
                  <h2 className="login-card-title">{t('auth.2fa.verify', 'Code bestätigen')}</h2>
                  <p className="login-card-sub">{t('auth.2fa.codeHelp', 'Gib den 6-stelligen Code aus deiner Authenticator-App ein.')}</p>
                </div>
                <div className="login-stage">
                  <OtpInput value={otpCode} onChange={setOtpCode} onComplete={submitOtp} disabled={isLoading} />
                  {error && (
                    <div className="login-error" role="alert"><span>{error}</span></div>
                  )}
                  <button
                    type="button" className="login-cta"
                    onClick={() => submitOtp(otpCode)}
                    disabled={isLoading || otpCode.length !== 6}
                    data-testid="login-otp-verify"
                  >
                    <span>{isLoading ? t('auth.signingIn') : t('auth.2fa.verify', 'Code bestätigen')}</span>
                  </button>
                  <button
                    type="button" className="login-linkbtn"
                    onClick={() => { setError(null); setStage({ kind: 'recovery', session_token: stage.session_token }) }}
                    data-testid="login-use-recovery"
                  >
                    {t('auth.2fa.useRecoveryCode', 'Recovery-Code stattdessen verwenden')}
                  </button>
                </div>
              </div>
            )}

            {/* Stage: recovery */}
            {stage.kind === 'recovery' && (
              <div data-testid="login-recovery-stage">
                <div className="login-card-head">
                  <h2 className="login-card-title">{t('auth.recovery.title', 'Recovery-Code eingeben')}</h2>
                </div>
                <div className="login-stage">
                  <input
                    type="text" className="login-input" placeholder="xxxxxxxx"
                    value={recoveryCode} onChange={(e) => setRecoveryCode(e.target.value)}
                    autoComplete="one-time-code" disabled={isLoading}
                    data-testid="login-recovery-input"
                  />
                  {error && (
                    <div className="login-error" role="alert"><span>{error}</span></div>
                  )}
                  <button
                    type="button" className="login-cta"
                    onClick={submitRecovery}
                    disabled={isLoading || recoveryCode.trim().length === 0}
                    data-testid="login-recovery-verify"
                  >
                    <span>{isLoading ? t('auth.signingIn') : t('auth.recovery.verify', 'Bestätigen')}</span>
                  </button>
                  <button
                    type="button" className="login-linkbtn"
                    onClick={() => { setError(null); setStage({ kind: 'otp', session_token: stage.session_token }); setRecoveryCode('') }}
                  >
                    ← {t('auth.2fa.useCode', 'Zurück zum OTP-Code')}
                  </button>
                </div>
              </div>
            )}

            {/* Stage: 2FA force-enrolment */}
            {stage.kind === 'setup' && (
              <TwoFactorSetup
                setupToken={stage.setup_token}
                onDone={() => navigate('/')}
                onCancel={() => {
                  setStage({ kind: 'password' })
                  setUsername(''); setPassword(''); setError(null)
                }}
              />
            )}

            <div className="login-card-foot">
              {t('login.supportText')} · <a href="mailto:support@heilport.app">support@heilport.app</a>
            </div>

          </div>
        </main>

        <div className="login-meta">
          © {new Date().getFullYear()} HEILPORT · <em>v{APP_VERSION}</em>
        </div>

      </div>
    </div>
  )
}
